import "./OrgOutcomesView.scss";

import { FC, useEffect, useRef, useState } from "react";

import { OutcomeTransferActions } from "./OutcomeTransferActions";

import { OUTCOME_ID_QUERY_PARAM } from "components/constants";
import { HideUnlessEditModeButton } from "components/materials/HideUnlessEditModeButton";
import { OutcomeCount } from "components/materials/home/admin/outcomes/OutcomeCount";
import { OutcomeGroupCard } from "components/materials/home/admin/outcomes/OutcomeGroupCard";
import { OutcomeChipList } from "components/materials/outcomes/OutcomeChipList";
import { OutcomeInfo } from "components/outcomes/OutcomeInfo";
import { OutcomeType } from "components/server-types";
import { t } from "i18n/i18n";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { storeApi, useAppSelector } from "store/index";
import {
  selectIsEditingAsOrgAdmin,
  selectNestedOutcomeGroups,
  selectOrgOutcomeRubric,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { useFindItemBySearchParamId } from "utils/hooks/use-find-item-by-search-param-id";
import { allocateUniqueTitle } from "utils/outcome";
import { useNavigate } from "utils/routing";
import { HomeRole, homeOutcomesUrl, withQueryParams } from "utils/urls";

export const AdminOutcomesView: FC = () => {
  const navigate = useNavigate();
  // Nested outcome group tree is fully rendered by OutcomeGroupCard.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const outcomeGroups = useListSelector(selectNestedOutcomeGroups);
  const canEdit = useAppSelector(selectIsEditingAsOrgAdmin);
  const [isCreatingOutcomeGroup, setIsCreatingOutcomeGroup] = useState(false);
  const isCreatingOutcomeGroupRef = useRef(false);

  const totalOutcomeCount = outcomeGroups.reduce((acc, group) => acc + group.outcomeCount, 0);

  const outcomes = outcomeGroups
    .map((outcomeGroup) => outcomeGroup.outcomes)
    .flat()
    .concat(
      outcomeGroups
        .map((outcomeGroup) => outcomeGroup.outcomeSubgroups)
        .flat()
        .map((subgroup) => subgroup.outcomes)
        .flat(),
    );

  const currentOutcome = useFindItemBySearchParamId(outcomes, OUTCOME_ID_QUERY_PARAM);

  // Rubric consumers need all levels/fields.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const rubric = useObjectSelector((s) => selectOrgOutcomeRubric(s, currentOutcome?.id));

  const onOutcomeChange = (outcome: OutcomeType) => {
    navigate(withQueryParams("", { [OUTCOME_ID_QUERY_PARAM]: outcome.id }));
  };

  useEffect(() => {
    storeApi.outcomes.list();

    storeApi.outcome_subgroups.list();

    storeApi.outcome_groups.list();
  }, []);

  const createFirstOutcome = () => {
    // If there are no outcome groups, create the default outcome group "Outcomes", and then create the first outcome.
    // TODO: If we need to create the default outcome group for user, we likely missed a set-up step and should fix it.
    // Note that the outcome's initial blank title will be replaced by the server with an auto-generated title.
    // The blank title is required in the context of unit tests, where server interactions are stubbed out.
    if (outcomeGroups.length === 0) {
      storeApi.outcome_groups
        .create({
          title: t("glossary.outcomes"),
        })
        .then((newOutcomeGroup) => {
          storeApi.outcomes
            .create({ outcome_group_id: newOutcomeGroup.id, title: "" })
            .then(onOutcomeChange);
        });
    } else {
      // Create the first outcome in the first outcome group.
      storeApi.outcomes
        .create({ outcome_group_id: outcomeGroups[0].id, title: "" })
        .then(onOutcomeChange);
    }
  };

  const isSmallScreen = !useIsMdOrLarger();

  if (isSmallScreen) {
    return (
      <div className="h-full overflow-y-auto pb-4">
        <div className="flex flex-col gap-4">
          <OutcomeTransferActions />
          <OutcomeChipList
            getOutcomeUrl={(outcome) => homeOutcomesUrl(HomeRole.ADMIN, outcome.id)}
            outcomes={outcomes}
            selectedOutcomeId={currentOutcome?.id}
          />

          <OutcomeInfo
            isSectionLevel={false}
            outcomeItem={currentOutcome}
            rubric={rubric}
            readOnly
          />
        </div>
      </div>
    );
  }

  const outcomeIndex = (
    <div className="flex h-full w-full">
      <div className="org-outcomes-sidebar h-full w-full flex-auto flex-col gap-4 overflow-y-auto bg-black-tint-99 px-3 py-4">
        <div className="flex justify-between">
          <h3 className="h3 my-0 px-2 py-1 text-black-tint-40">{t("outcomes.total_outcomes")}</h3>
          <div className="flex">
            <OutcomeCount count={totalOutcomeCount} />
          </div>
        </div>

        {outcomeGroups.map((group) => (
          <OutcomeGroupCard
            currentOutcome={currentOutcome}
            key={group.id}
            outcomeGroup={group}
            queryParam={OUTCOME_ID_QUERY_PARAM}
          />
        ))}

        {canEdit && (
          <HideUnlessEditModeButton
            className="add-group"
            eventName="Org Outcomes - Add group"
            isDisabled={isCreatingOutcomeGroup}
            reference="outcome_group"
            title={t("common.add.outcome_group")}
            onClick={async () => {
              if (isCreatingOutcomeGroupRef.current) return;

              isCreatingOutcomeGroupRef.current = true;
              setIsCreatingOutcomeGroup(true);
              const untitledGroup = t("common.untitled.outcome_group");
              try {
                await storeApi.outcome_groups.create({
                  title: allocateUniqueTitle(
                    untitledGroup,
                    outcomeGroups.map((group) => group.title),
                  ),
                });
              } finally {
                isCreatingOutcomeGroupRef.current = false;
                setIsCreatingOutcomeGroup(false);
              }
            }}
          />
        )}
      </div>

      <main className="h-full flex-auto">
        <OutcomeInfo
          createNewOutcome={outcomes.length === 0 ? createFirstOutcome : undefined}
          isSectionLevel={false}
          outcomeItem={currentOutcome}
          readOnly={!canEdit}
          rubric={rubric}
        />
      </main>
    </div>
  );

  return (
    <div className="flex h-full w-full flex-col">
      <OutcomeTransferActions />
      <div className="min-h-0 flex-1">{outcomeIndex}</div>
    </div>
  );
};
